import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LazyLoad from "react-lazy-load";

import { Box, Grid, makeStyles, Select, MenuItem } from "@material-ui/core";

import { Button } from "../../../../components/base";
import { IconTitle, NoDataCard, allBuildingsItems } from "../../../../components/parts";
import ElectricityImg from "../../../../assets/images/icons/electricity.svg";
import BellImg from "../../../../assets/images/icons/bell_black.svg";
import TenancyImg from "../../../../assets/images/icons/tenancy.svg";
import { ActionButtonsGroup } from "../../../../components/buttonsGroup/ActionButtonsGroup";
import ProfitabilityImg from "../../../../assets/images/icons/profit.svg";
import { tenancyChartLines } from "../../../building/tabsV2/tenants/sample";
import { List } from "./List";
import { getProfitbility, getTenantsList } from "../../../../redux/tenants/actions";
import { RiskProfile } from "./AllBuildingsRisk";
import { AllBuildingTenantConnectionStatus } from "./ConnectionStatusComponent";
import {
  getData,
  capitalize,
  getChartTicks,
  yearFromMonths,
  avgFromArrayOfNumbers,
  dmyUnit,
} from "../../../../services";
import { TenancyDataInfo } from "../../../building/tabsV2/tenants/tenancyData/tenancyDataInfo";
import { TenancyChart } from "../../../building/tabsV2/tenants/tenancyData/tenancyChart";
import { PortfolioAlert } from "../../../building/tabsV2/tenants/portfolioAlerts";
import { TenantPayments } from "../../../building/tabsV2/tenants/electricity/tenantPayments";
import { TenantPaymentsReport } from "../../../building/tabsV2/tenants/electricity/tenantPaymentsReport";

const TenancyNoDataCard = ({ title }) => (
  <NoDataCard title={title} footerNames={["Avg. portfolio", "Avg. Industry"]} type="line" />
);

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: theme.spacing(4),
  },
  titleContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: "bold",
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
  },
  tenantListButton: {
    marginLeft: theme.spacing(2.5),
  },
  boxTop: {
    padding: `${theme.spacing(2)}px ${theme.spacing(2)}px 0px`,
  },
}));

export const TenantsTab = ({ buildings }) => {
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const [showList, setShowList] = useState(false);
  const [profitabilityYearOption, setProfitabilityYearOption] = useState();
  const [profitabilityYearOptions, setProfitabilityYearOptions] = useState([]);
  const [yearlyRevenueData, setYearlyRevenueData] = useState([]);
  const [yearlyTradingRevenueData, setYearlyTradingRevenueData] = useState([]);
  const [yearlyUnrealizedRevenueData, setYearlyUnrealizedRevenueData] = useState([]);
  const [tenancyAgingData, setTenancyAgingData] = useState([]);
  const [tenancyEndData, setTenancyEndData] = useState([]);
  const [tenantsAvgMonthlyConsumptionData, setTenantsAvgMonthlyConsumptionData] = useState([]);
  const [leaseSquareData, setLeaseSquareData] = useState([]);
  const { cluster, segments, selectedCountries } = useSelector((state) => state.selectCSC);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getProfitbility());
  }, [dispatch, cluster, segments, selectedCountries]);

  const getDataForMonthlyLeaseChart = (setChartData) => {
    getData(`/praas/v1/buildings/monthly-lease/`, (data) => {
      const result = {};
      ["portfolio", "industry"].forEach((key) => {
        data[key].forEach((e) => {
          result[e.rent] = result[e.rent] || {};
          result[e.rent][key] = e.percent;
        });
      });

      setChartData(
        Object.keys(result).map((e) => {
          return {
            x: `${currencySymbol}${e}`,
            ...result[e],
          };
        }),
      );
    });
  };

  const getDataForTenancyChart = (type, setChartData) => {
    getData(`/praas/v1/buildings/${type}/`, (data) => {
      const result = {};
      ["portfolio", "industry"].forEach((key) => {
        ["days", "months"].forEach((d) => {
          data[key][d].forEach((e) => {
            result[d[0] + e[d]] = result[d[0] + e[d]] || {};
            result[d[0] + e[d]][key] = e.percent;
          });
        });
      });

      const group = {};
      Object.keys(result).map((key) => {
        let year = null;
        if (key.includes("m")) {
          let monthWithoutM = key.replace("m", "");
          year = yearFromMonths(monthWithoutM);
        }
        if (year) {
          const yearlyKey = year * 12 + 6;
          if (!(yearlyKey in group)) {
            group["m" + yearlyKey] = [];
          }
          group["m" + yearlyKey].push(result[key]);
          delete result[key];
        } else {
          group[key] = result[key];
        }
      });

      Object.keys(group).map((key) => {
        if (Array.isArray(group[key])) {
          const pPercentages = [];
          const iPercentages = [];
          group[key].forEach((item) => {
            if (item["portfolio"]) pPercentages.push(item["portfolio"]);
            if (item["industry"]) iPercentages.push(item["industry"]);
          });
          group[key] = {};
          if (pPercentages.length) group[key]["portfolio"] = avgFromArrayOfNumbers(pPercentages);
          if (iPercentages.length) group[key]["industry"] = avgFromArrayOfNumbers(iPercentages);
        }
      });

      setChartData(
        Object.keys(group)
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
          .map((e) => {
            return {
              x: dmyUnit(e, type === "tenancy-aging" ? "" : "in"),
              ...group[e],
            };
          }),
      );
    });
  };

  useEffect(() => {
    getDataForMonthlyLeaseChart(setLeaseSquareData);
    getDataForTenancyChart("tenancy-aging", setTenancyAgingData);
    getDataForTenancyChart("tenancy-ends", setTenancyEndData);
  }, [cluster, segments, selectedCountries]);

  const createChartData = (data, setChartData, unit = currencySymbol) => {
    if (!data.portfolio) {
      setChartData([]);
      return;
    }
    const result = {};
    ["portfolio", "industry"].forEach((key) => {
      Object.keys(data[key]).forEach((e) => {
        result[e] = result[e] || {};
        result[e][key] = data[key][e];
      });
    });
    setChartData(
      Object.keys(result).map((e) => ({
        x: `${unit}${e}`,
        ...result[e],
      })),
    );
  };

  useEffect(() => {
    const year = profitabilityYearOption?.label;
    getData(`/praas/v1/buildings/tenants-profitability/${year ? `?year=${year}` : ""}`, (data) => {
      if (!profitabilityYearOptions.length) {
        setProfitabilityYearOptions(
          data.years.map((year, i) => ({
            id: i,
            label: year,
          })),
        );
      }

      createChartData(data.total_revenue, setYearlyRevenueData);
      createChartData(data.trading_revenue, setYearlyTradingRevenueData);
      createChartData(data.unrealized_revenue, setYearlyUnrealizedRevenueData);
    });

    getData("/praas/v1/buildings/tenants-avg-monthly-consumption/", (data) => {
      createChartData(data, setTenantsAvgMonthlyConsumptionData, "");
    });

    return () => {
      setYearlyRevenueData([]);
      setYearlyTradingRevenueData([]);
      setYearlyUnrealizedRevenueData([]);
      setTenantsAvgMonthlyConsumptionData([]);
    };
  }, [profitabilityYearOption, cluster, segments, selectedCountries]);

  if (showList) {
    return <List building={buildings} onBack={setShowList} />;
  }

  return (
    <Box className={classes.root}>
      <Grid
        className={classes.titleContainer}
        container
        justifyContent="space-between"
        alignItems="center"
      >
        <IconTitle
          iconSrc={TenancyImg}
          title="TENANCY DATA & ANALYTICS"
          rootStyle={{
            marginTop: 0,
            marginBottom: 0,
          }}
        />
        <ActionButtonsGroup hideIntegrateBtn />
        <Button
          text="TENANTS LIST"
          rootStyle={classes.tenantListButton}
          onClick={() => {
            setShowList(true);
            dispatch(getTenantsList());
          }}
        />
      </Grid>

      <TenancyDataInfo />

      <Grid container spacing={3} style={{ marginTop: 26, minHeight: 384 }}>
        <Grid item xs={4}>
          {leaseSquareData.length ? (
            <TenancyChart
              footerItems={allBuildingsItems}
              title={`Average monthly Lease/${user.unit_system}`}
              chartData={leaseSquareData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(leaseSquareData, 5)}
            />
          ) : (
            <TenancyNoDataCard title={`Average monthly Lease/${user.unit_system}`} />
          )}
        </Grid>
        <Grid item xs={4}>
          {tenancyAgingData.length ? (
            <TenancyChart
              footerItems={allBuildingsItems}
              title="Tenancy Aging"
              chartData={tenancyAgingData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(tenancyAgingData, 4)}
            />
          ) : (
            <TenancyNoDataCard title="Tenancy Aging" />
          )}
        </Grid>
        <Grid item xs={4}>
          {tenancyEndData.length ? (
            <TenancyChart
              footerItems={allBuildingsItems}
              title="Tenancy Ends"
              chartData={tenancyEndData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(tenancyEndData, 4)}
            />
          ) : (
            <TenancyNoDataCard title="Tenancy Ends" />
          )}
        </Grid>
      </Grid>

      <IconTitle
        rootStyle={{ paddingTop: 20, marginBottom: 25 }}
        iconSrc={ElectricityImg}
        title="ELECTRICITY DATA & ANALYTICS"
      />
      <LazyLoad>
        <AllBuildingTenantConnectionStatus />
      </LazyLoad>

      <Grid container spacing={3} style={{ marginTop: 26, minHeight: 384 }}>
        <Grid item xs={4}>
          {tenantsAvgMonthlyConsumptionData.length ? (
            <TenancyChart
              title={`Tenants Avg. Monthly Consumption (kWh/${user.unit_system})`}
              chartData={tenantsAvgMonthlyConsumptionData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(tenantsAvgMonthlyConsumptionData, 3)}
              footerItems={allBuildingsItems}
            />
          ) : (
            <TenancyNoDataCard
              title={`Tenants Avg. Monthly Consumption (kWh/${user.unit_system})`}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          <LazyLoad>
            <TenantPayments />
          </LazyLoad>
        </Grid>
        <Grid item xs={4}>
          <LazyLoad>
            <TenantPaymentsReport />
          </LazyLoad>
        </Grid>
      </Grid>

      <Grid container>
        <IconTitle
          iconSrc={ProfitabilityImg}
          title="TENANTS PROFITABILITY"
          rootStyle={{
            marginRight: 10,
          }}
        />
        {profitabilityYearOptions?.length ? (
          <Select
            labelId="label"
            id="select"
            defaultValue={profitabilityYearOptions[0].id}
            autoWidth={true}
            disableUnderline
            classes={{ root: classes.selectRoot }}
            onChange={(e) => setProfitabilityYearOption(profitabilityYearOptions[e.target.value])}
          >
            {profitabilityYearOptions.map((option, i) => (
              <MenuItem key={i} value={option.id} style={{ fontSize: 12 }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        ) : null}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={4}>
          {yearlyRevenueData.length ? (
            <TenancyChart
              footerItems={allBuildingsItems}
              title={`Total Yearly Revenue/${capitalize(user.unit_system)}`}
              chartData={yearlyRevenueData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(yearlyRevenueData, 3)}
            />
          ) : (
            <TenancyNoDataCard title={`Total Yearly Revenue/${capitalize(user.unit_system)}`} />
          )}
        </Grid>
        <Grid item xs={4}>
          {yearlyTradingRevenueData.length ? (
            <TenancyChart
              footerItems={allBuildingsItems}
              title={`PRaaS Trading Revenues/${capitalize(user.unit_system)} - per year`}
              chartData={yearlyTradingRevenueData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(yearlyTradingRevenueData, 3)}
            />
          ) : (
            <TenancyNoDataCard
              title={`PRaaS Trading Revenues/${capitalize(user.unit_system)} - per year`}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          {yearlyUnrealizedRevenueData.length ? (
            <TenancyChart
              footerItems={allBuildingsItems}
              title={`Unrealized PRaaS Revenues/${capitalize(user.unit_system)} - per year`}
              chartData={yearlyUnrealizedRevenueData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(yearlyUnrealizedRevenueData, 3)}
            />
          ) : (
            <TenancyNoDataCard
              title={`Unrealized PRaaS Revenues/${capitalize(user.unit_system)} - per year`}
            />
          )}
        </Grid>
      </Grid>

      <LazyLoad>
        <RiskProfile />
      </LazyLoad>

      <IconTitle iconSrc={BellImg} title="PORTFOLIO ALERTS" />
      <LazyLoad>
        <PortfolioAlert />
      </LazyLoad>
    </Box>
  );
};
